import {
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  Output,
} from '@angular/core';
import { Haptics } from '@freelancer/haptics';

/**
 * WhatsApp/iMessage-style swipe-to-reply gesture.
 *
 * Bind to the element that should follow the finger (e.g. a chat bubble). A
 * short rightward swipe translates the host, reveals progress towards the
 * trigger threshold, and emits `swipeReply` when released past it.
 *
 * Implemented with native Pointer Events rather than HammerJS: the host is
 * given `touch-action: pan-y`, so the browser keeps handling vertical scrolling
 * natively and only hands us horizontal movement. (HammerJS, as configured
 * globally by Angular, forces `touch-action: none` and calls `preventDefault`
 * on touchmove, which would break scrolling on a scroll surface like a message
 * list.)
 */
@Directive({
  selector: '[flSwipeToReply]',
  standalone: true,
  exportAs: 'flSwipeToReply',
})
export class SwipeToReplyDirective {
  private readonly haptics = inject(Haptics);

  /** Disables the gesture entirely (e.g. desktop, failed/removed messages). */
  @Input('flSwipeToReplyDisabled') disabled = false;

  /** Emitted once when the bubble is released past the trigger threshold. */
  @Output() swipeReply = new EventEmitter<void>();

  /** Horizontal travel (px) required before we commit to a swipe. */
  private readonly activationThreshold = 8;
  /** Distance (px) the bubble must travel to trigger a reply. */
  private readonly triggerThreshold = 48;
  /** Distance (px) at which the bubble stops following the finger 1:1. */
  private readonly maxTranslate = 72;
  /** How much extra movement leaks through once past `maxTranslate`. */
  private readonly rubberBandFactor = 0.2;

  /** 0..1 progress towards the trigger threshold, for reveal affordances. */
  progress = 0;
  /** True once the current swipe has passed the trigger threshold. */
  armed = false;

  private translateX = 0;
  private startX = 0;
  private startY = 0;
  /** A pointer is down and we are still deciding the gesture direction. */
  private tracking = false;
  /** The gesture has been confirmed as a horizontal rightward swipe. */
  private active = false;

  @HostBinding('style.touch-action')
  get touchAction(): string {
    // Let the browser own vertical scrolling; we only want horizontal swipes.
    return this.disabled ? '' : 'pan-y';
  }

  @HostBinding('style.transform')
  get transform(): string {
    return `translateX(${this.translateX}px)`;
  }

  @HostBinding('style.transition')
  get transition(): string {
    // Follow the finger during the drag, ease back on release.
    return this.active ? 'none' : 'transform 0.2s ease-out';
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (this.disabled || event.pointerType === 'mouse') {
      return;
    }
    this.tracking = true;
    this.active = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.tracking || this.disabled) {
      return;
    }

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (!this.active) {
      // A vertical drag is the browser's to scroll; bow out of this gesture.
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.tracking = false;
        return;
      }
      // Wait until there is enough rightward movement to commit.
      if (deltaX < this.activationThreshold) {
        return;
      }
      this.active = true;
      (event.target as Element).setPointerCapture?.(event.pointerId);
    }

    const distance = Math.max(0, deltaX);
    this.translateX = this.applyRubberBand(distance);
    this.progress = Math.min(distance / this.triggerThreshold, 1);

    const armed = distance >= this.triggerThreshold;
    if (armed && !this.armed) {
      // Rising edge: nudge the user that releasing now will reply.
      this.haptics.light();
    }
    this.armed = armed;
  }

  @HostListener('pointerup')
  @HostListener('pointercancel')
  onPointerEnd(): void {
    if (this.active && this.armed) {
      this.swipeReply.emit();
    }
    this.reset();
  }

  private applyRubberBand(deltaX: number): number {
    if (deltaX <= this.maxTranslate) {
      return deltaX;
    }
    return (
      this.maxTranslate + (deltaX - this.maxTranslate) * this.rubberBandFactor
    );
  }

  private reset(): void {
    this.tracking = false;
    this.active = false;
    this.translateX = 0;
    this.progress = 0;
    this.armed = false;
  }
}
